import { EVENTS } from '../../constants';
import type { KnowledgeAttachment } from '../../types/knowledge';

type TrackFn = (eventName: string, properties?: Record<string, unknown>) => void;
type WarnFn = (message: string, details?: Record<string, unknown>) => void;

interface AttachmentOpenParams {
  attachment: KnowledgeAttachment | null | undefined;
  knowledgeItemId?: string | number;
}

/**
 * Analytics for opening a knowledge item's attachment. `attachmentOpen` guards its payload,
 * logs through `warn` when the attachment or its knowledge item id is missing instead of sending
 * a broken event, and never lets a failure inside analytics reach the screen.
 *
 * @param track - Sends one event, e.g. the app's `analyticsService.track`.
 * @param warn - Reports a skipped or failed event, e.g. the app's logger.
 * @returns `attachmentOpen`.
 * @example
 * const reporter = createAttachmentReporter(
 *   (name, properties) => analyticsService.track(name, properties),
 *   (message, details) => logger.warn(message, details),
 * );
 * reporter.attachmentOpen({ attachment, knowledgeItemId: knowledge.id });
 */
export const createAttachmentReporter = (track: TrackFn, warn: WarnFn) => ({
  attachmentOpen: ({ attachment, knowledgeItemId }: AttachmentOpenParams) => {
    if (!attachment?._id || !knowledgeItemId) {
      warn(`[analytics] ${EVENTS.KNOWLEDGE_ITEMS.ATTACHMENT_OPEN} skipped: payload not loaded`, {
        hasAttachment: Boolean(attachment?._id),
        hasKnowledgeItem: Boolean(knowledgeItemId),
      });
      return;
    }
    try {
      track(EVENTS.KNOWLEDGE_ITEMS.ATTACHMENT_OPEN, {
        attachmentId: attachment._id,
        knowledgeItemId: knowledgeItemId.toString(),
      });
    } catch (error) {
      warn(`[analytics] ${EVENTS.KNOWLEDGE_ITEMS.ATTACHMENT_OPEN} failed`, {
        error: String(error),
      });
    }
  },
});

export type AttachmentReporter = ReturnType<typeof createAttachmentReporter>;
